import React, { useState, useEffect, useContext } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'i18n'
import { SStyled, useSTheme } from 'styles/theme'
import { StepWizardChildProps } from 'react-step-wizard'

import SBox from 'components/opinionless/SBox'
import SText from 'components/opinionless/SText'
import SInputError from 'components/opinionless/SInputErrorV2'
import { LBButton, LBText } from 'containers/Apply/steps/ApplicationStepLBIntroduction'
import FButtonPrevious from 'components/BaseComponents/FButton/FPreviousButton'
import XBanner from 'v2/components/XBanner'
import FImage from 'components/BaseComponents/FImage'

import { formatMoney } from 'utils/money'
import { formatAddress } from 'utils/address'
import { formattedDateMMDDYYYY } from 'utils/date'
import { ga4_useTitle } from 'utils/ga4'
import { getAge } from 'utils/date'
import { anualIncomeData } from 'containers/Enrollment/HouseholdIncome/HouseholdIncomeEligibility'

import { useHouseholdMembers } from 'v2/hooks/useHouseholdMembers'
import { ApplicationContext } from 'v2/context/application'
import { useApplicationBasicInfo } from 'v2/hooks/useApplicationBasicInfo'
import { useApplicationAnswers } from 'v2/hooks/useApplicationAnswers'
import { useBenefitsAnswers } from 'v2/hooks/useBenefitsAnswers'

import style from './style.module.scss'

import { WIZARD_STEPS_MAPPING } from 'containers/Apply/LongBeachWizard'
import { updatePercentageAndStep } from 'v2/components/XProgressBar/utils'

const LBTextHeader = SStyled(SText)`
    ${(p) => `
        @media screen and (max-width: ${p.theme.breakpoints[0]}) {
            font-size: 24px;
            line-height: 130%;
        }
    `}
`

export const Container = SStyled(SBox)`
    width: 100%;
    max-width: 600px;
`

interface Props {
    setCurrentStep?: (number) => void
    applicationId: string
    personId: string
    fontFamily: any
    onNextStep?: any
    onPrevious?: any
    updatePercentage: any
}

const ReviewSummary: React.FC<Props> = (props: Props & StepWizardChildProps) => {
    const applicationContext = useContext(ApplicationContext)

    const {
        loading: householdMembersLoading,
        error: householdMembersError,
        refetch: householdMembersRefetch,
    } = useHouseholdMembers()
    const { refetch: basicInfoRefetch } = useApplicationBasicInfo()

    const {
        loading: applicationAnswersLoading,
        refetch: refetchApplicationAnswers,
        getAnswersByCategory,
    } = useApplicationAnswers()
    const {
        refetch: refetchBenefitsAnswers,
        loading: benefitsAnswersLoading,
        getBenefitsAnswersByCategory,
    } = useBenefitsAnswers()

    useEffect(() => {
        refetchApplicationAnswers()
        if (applicationContext?.profile?.person?.id) {
            refetchBenefitsAnswers()
        }

        householdMembersRefetch()
        basicInfoRefetch()
    }, [])

    const theme = useSTheme()
    const { t } = useTranslation(window.LANG_NS)
    const { handleSubmit, errors } = useForm()
    const [householdMembersDetails, setHouseholdMembersDetails] = useState([])

    const [loading, setLoading] = useState(true)

    const fontFamily = props.fontFamily

    useEffect(() => {
        if (!householdMembersLoading) {
            setLoading(false)
            setHouseholdMembersDetails(
                applicationContext.householdMembers.map((member) => ({
                    id: member.id,
                    name: member.name,
                    relation: member.relation,
                    dob: member.dob,
                    blindDisabled: member.blindDisabled,
                    earnedIncome: member.earnedIncome,
                    unearnedIncome: member.unearnedIncome,
                }))
            )
        }
    }, [applicationContext.householdMembers])

    ga4_useTitle('Review Summary')

    const pregnancyResponse = getAnswersByCategory('PREGNANCY')?.[0]?.response
    const housingStatusResponse = getAnswersByCategory('HOUSING_STATUS')?.[0]?.response
    const hasAppliedToOtherProgramsResponse = getAnswersByCategory('HAS_APPLIED_TO_OTHER_PROGRAMS')?.[0]?.response
    const householdIncomeChoice = getAnswersByCategory('INCOME_THRESHOLD')?.[0]?.response

    const SSIBenefitsAnswer = getAnswersByCategory('SSI_BENEFITS')?.[0]?.response
    const HUDHousingBenefitsAnswer = getBenefitsAnswersByCategory('HOUSING_ASSISTANCE')?.[0]?.answer
        ? `${formatMoney(getBenefitsAnswersByCategory('HOUSING_ASSISTANCE')?.[0]?.answer)} ${t('per_month')}`
        : '-'
    const CalFreshBenefitsAnswer = getBenefitsAnswersByCategory('CAL_FRESH_AMOUNT')?.[0]?.answer
        ? `${formatMoney(getBenefitsAnswersByCategory('CAL_FRESH_AMOUNT')?.[0]?.answer)} ${t('per_month')}`
        : '-'
    const CalWorksBenefitsAnswer = getBenefitsAnswersByCategory('CAL_WORKS_AMOUNT')?.[0]?.answer
        ? `${formatMoney(getBenefitsAnswersByCategory('CAL_WORKS_AMOUNT')?.[0]?.answer)} ${t('per_month')}`
        : '-'
    const HeadStartBenefitsAnswer = t(getBenefitsAnswersByCategory('HEAD_START_RE_QUALIFYING')?.[0]?.answer)
    const ACPBenefitsAnswer = t(getBenefitsAnswersByCategory('ACP_HOW')?.[0]?.answer)

    const ProofOfResidenceDocumentAnswer = t(
        `${getAnswersByCategory('PROOF_OF_RESIDENCY_CATEGORY')?.[0]?.response}_unstyled`
    ).replace(/<\/?b>/g, '')
    const ProofOfIncomeDocumentAnswer = getAnswersByCategory('PROOF_OF_INCOME_CATEGORY')?.[0]?.response
        ? t(`${getAnswersByCategory('PROOF_OF_INCOME_CATEGORY')?.[0]?.response}_unstyled`).replace(/<\/?b>/g, '')
        : '-'

    const { percentage, wizardStep, childWizzardStep } = updatePercentageAndStep(
        WIZARD_STEPS_MAPPING.review,
        WIZARD_STEPS_MAPPING.review.introduction,
        WIZARD_STEPS_MAPPING
    )

    const addressComponents = [
        applicationContext?.location?.addressLine1,
        applicationContext?.location?.addressLine2,
        applicationContext?.location?.city,
        applicationContext?.location?.state,
        applicationContext?.location?.zipCode,
    ]

    const applicantAddress = formatAddress(addressComponents)

    props.updatePercentage(percentage, wizardStep, childWizzardStep)
    if (loading) {
        return null
    }
    return (
        <Container>
            <div className={style.review_container}>
                <SBox as="form" onSubmit={handleSubmit(props.nextStep)}>
                    <FButtonPrevious onClick={props.onPrevious} />
                    <LBTextHeader
                        sx={{
                            textAlign: 'left',
                            fontSize: '32px',
                            color: theme.colors.secondaryColor,
                            lineHeight: '120%',
                            fontFamily: fontFamily.header,
                            marginBottom: '24px',
                        }}
                    >
                        {t('long_beach_review_summary_header')}
                    </LBTextHeader>
                    <XBanner
                        title={t('long_beach_household_income_review_banner_title')}
                        description={t('long_beach_household_income_review_banner_description')}
                        isButton={false}
                        type="warning"
                    />
                    <LBTextHeader
                        sx={{
                            textAlign: 'left',
                            fontSize: '32px',
                            color: theme.colors.blackLabelfont,
                            lineHeight: '120%',
                            fontFamily: fontFamily.header,
                            marginBottom: '24px',
                            marginTop: '24px',
                        }}
                    >
                        {t('applicant_information')}
                    </LBTextHeader>
                    <LBText
                        sx={{
                            textAlignLast: 'left',
                            whiteSpace: 'pre-line',
                            fontFamily: fontFamily.body,
                            fontWeight: '400',
                            lineHeight: '140%',
                            color: '#6C6C6C',
                            fontSize: '14px',
                        }}
                        dangerouslySetInnerHTML={{
                            __html: t('long_beach_review_applicant_summary_details', {
                                name: `${applicationContext?.profile?.firstName} ${applicationContext?.profile?.lastName}`,
                                address: applicantAddress.length > 1 ? applicantAddress : '-',
                                phone_number: `${applicationContext?.communication?.phoneNumber}`,
                                email: `${applicationContext?.communication?.email}`,
                                dob: `${formattedDateMMDDYYYY(applicationContext?.profile?.dob)}`,
                                language: `${
                                    window.LANGUAGES.find((i) => i.code === applicationContext?.profile?.language)?.text
                                }`,
                                ethnicity: `${t(applicationContext?.profile?.ethnicity)}`,
                                blind_disabled: `${applicationContext?.profile?.blindDisabled ? t('yes') : t('no')}`,
                            }),
                        }}
                    />
                    <LBText
                        sx={{
                            textAlignLast: 'left',
                            whiteSpace: 'pre-line',
                            fontFamily: fontFamily.body,
                            fontWeight: '700',
                            lineHeight: '140%',
                            color: '#6C6C6C',
                            fontSize: '14px',
                        }}
                        dangerouslySetInnerHTML={{
                            __html: t('select_all_that_apply'),
                        }}
                    />
                    <LBText
                        sx={{
                            textAlignLast: 'left',
                            whiteSpace: 'pre-line',
                            fontFamily: fontFamily.body,
                            fontWeight: '400',
                            lineHeight: '140%',
                            color: '#6C6C6C',
                            fontSize: '14px',
                            marginTop: '0px !important',
                        }}
                        dangerouslySetInnerHTML={{
                            __html: pregnancyResponse
                                ?.split(',')
                                .map((element) => `${t(element.trim())}`)
                                .join('\n'),
                        }}
                    />
                    <LBText
                        sx={{
                            textAlignLast: 'left',
                            whiteSpace: 'pre-line',
                            fontFamily: fontFamily.body,
                            fontWeight: '700',
                            lineHeight: '140%',
                            color: '#6C6C6C',
                            fontSize: '14px',
                        }}
                        dangerouslySetInnerHTML={{
                            __html: t('housing_status_question'),
                        }}
                    />
                    <LBText
                        sx={{
                            textAlignLast: 'left',
                            whiteSpace: 'pre-line',
                            fontFamily: fontFamily.body,
                            fontWeight: '400',
                            lineHeight: '140%',
                            color: '#6C6C6C',
                            fontSize: '14px',
                            marginTop: '0px !important',
                        }}
                        dangerouslySetInnerHTML={{
                            __html: `${t(housingStatusResponse)}`,
                        }}
                    />
                    <LBText
                        sx={{
                            textAlignLast: 'left',
                            whiteSpace: 'pre-line',
                            fontFamily: fontFamily.body,
                            fontWeight: '700',
                            lineHeight: '140%',
                            color: '#6C6C6C',
                            fontSize: '14px',
                        }}
                        dangerouslySetInnerHTML={{
                            __html: t('has_applied_to_other_programs_question'),
                        }}
                    />
                    <LBText
                        sx={{
                            textAlignLast: 'left',
                            whiteSpace: 'pre-line',
                            fontFamily: fontFamily.body,
                            fontWeight: '400',
                            lineHeight: '140%',
                            color: '#6C6C6C',
                            fontSize: '14px',
                            marginTop: '0px !important',
                        }}
                        dangerouslySetInnerHTML={{
                            __html: `${t(hasAppliedToOtherProgramsResponse)}`,
                        }}
                    />
                    <LBTextHeader
                        sx={{
                            textAlign: 'left',
                            fontSize: '32px',
                            color: theme.colors.blackLabelfont,
                            lineHeight: '120%',
                            fontFamily: fontFamily.header,
                            marginBottom: '24px',
                            marginTop: '24px',
                        }}
                    >
                        {t('long_beach_household_income_header')}
                    </LBTextHeader>
                    <LBText
                        sx={{
                            textAlignLast: 'left',
                            whiteSpace: 'pre-line',
                            fontFamily: fontFamily.body,
                            fontWeight: '400',
                            lineHeight: '140%',
                            color: '#6C6C6C',
                            fontSize: '14px',
                        }}
                        dangerouslySetInnerHTML={{
                            __html: t('long_beach_review_household_number_summary_details', {
                                household_member_number: `${applicationContext.householdMembers.length + 1}`,
                            }),
                        }}
                    />
                    <LBText
                        sx={{
                            textAlignLast: 'left',
                            whiteSpace: 'pre-line',
                            fontFamily: fontFamily.body,
                            fontWeight: '700',
                            lineHeight: '140%',
                            color: '#6C6C6C',
                            fontSize: '14px',
                        }}
                        dangerouslySetInnerHTML={{
                            __html: t('long_beach_program_household_choices_review_screen'),
                        }}
                    />
                    <LBText
                        sx={{
                            textAlignLast: 'left',
                            whiteSpace: 'pre-line',
                            fontFamily: fontFamily.body,
                            fontWeight: '400',
                            lineHeight: '140%',
                            color: '#6C6C6C',
                            fontSize: '14px',
                            marginTop: '0px !important',
                        }}
                        dangerouslySetInnerHTML={{
                            __html: `${t(householdIncomeChoice, {
                                householdCombinedIncome: `${formatMoney(
                                    anualIncomeData[applicationContext.householdMembers.length + 1]
                                )}`,
                            })}`,
                        }}
                    />
                    <div className={style.household_applicant_review_container}>
                        <div className={style.household_applicant_review_container__icon}>
                            <FImage imageUrl={'/lb_household_income_person.svg'} alt="person" />
                        </div>
                        <div className={style.household_applicant_review_container__applicant_group}>
                            <LBText
                                sx={{
                                    textAlignLast: 'left',
                                    whiteSpace: 'pre-line',
                                    fontFamily: fontFamily.body,
                                    fontWeight: '700',
                                    lineHeight: '140%',
                                    color: theme.colors.lbBlue,
                                    fontSize: '14px',
                                }}
                                dangerouslySetInnerHTML={{
                                    __html: t('long_beach_applicant_name_label_relationship', {
                                        name: `${applicationContext.profile?.firstName} ${applicationContext?.profile?.lastName}`.toUpperCase(),
                                        relationship: t('long_beach_household_applicant_label'),
                                    }),
                                }}
                            />
                            <LBText
                                sx={{
                                    textAlignLast: 'left',
                                    whiteSpace: 'pre-line',
                                    fontFamily: fontFamily.body,
                                    fontWeight: '400',
                                    lineHeight: '140%',
                                    color: theme.colors.gray400,
                                    fontSize: '14px',
                                    marginBottom: '0px',
                                }}
                                dangerouslySetInnerHTML={{
                                    __html: `${t('dob_placeholder')}: ${formattedDateMMDDYYYY(
                                        applicationContext?.profile?.dob
                                    )} (${t('ssi_benefits_summary_table_key_age')} ${getAge(
                                        applicationContext?.profile?.dob
                                    )})`,
                                }}
                            />
                            <LBText
                                sx={{
                                    textAlignLast: 'left',
                                    whiteSpace: 'pre-line',
                                    fontFamily: fontFamily.body,
                                    fontWeight: '400',
                                    lineHeight: '140%',
                                    color: theme.colors.gray400,
                                    fontSize: '14px',
                                    marginBottom: '0px',
                                    marginTop: '0px !important',
                                }}
                                dangerouslySetInnerHTML={{
                                    __html: t(
                                        `${
                                            applicationContext?.profile?.blindDisabled
                                                ? 'ssi_benefits_wizard_blind_disabled_yes'
                                                : 'ssi_benefits_wizard_blind_disabled_no'
                                        }`
                                    ),
                                }}
                            />
                            <LBText
                                sx={{
                                    textAlignLast: 'left',
                                    whiteSpace: 'pre-line',
                                    fontFamily: fontFamily.body,
                                    fontWeight: '400',
                                    lineHeight: '140%',
                                    color: theme.colors.gray400,
                                    fontSize: '14px',
                                    marginBottom: '0px',
                                    marginTop: '0px !important',
                                }}
                                dangerouslySetInnerHTML={{
                                    __html:
                                        applicationContext?.profile?.earnedIncome !== '0'
                                            ? t(`lb_household_income_review_income_earner_label`)
                                            : null,
                                }}
                            />
                            <LBText
                                sx={{
                                    textAlignLast: 'left',
                                    whiteSpace: 'pre-line',
                                    fontFamily: fontFamily.body,
                                    fontWeight: '400',
                                    lineHeight: '140%',
                                    color: theme.colors.gray400,
                                    fontSize: '14px',
                                    marginBottom: '0px',
                                    marginTop: '0px !important',
                                }}
                                dangerouslySetInnerHTML={{
                                    __html: `${t('ssi_benefits_summary_table_key_earned_income')}: ${formatMoney(
                                        applicationContext?.profile?.earnedIncome
                                    )}`,
                                }}
                            />
                            <LBText
                                sx={{
                                    textAlignLast: 'left',
                                    whiteSpace: 'pre-line',
                                    fontFamily: fontFamily.body,
                                    fontWeight: '400',
                                    lineHeight: '140%',
                                    color: theme.colors.gray400,
                                    fontSize: '14px',
                                    marginBottom: '0px',
                                    marginTop: '0px !important',
                                }}
                                dangerouslySetInnerHTML={{
                                    __html: `${t('ssi_benefits_summary_table_key_unearned_income')}: ${formatMoney(
                                        applicationContext?.profile?.unearnedIncome
                                    )}`,
                                }}
                            />
                        </div>
                    </div>
                    {householdMembersDetails.map((member, index) => (
                        <div className={style.household_member_review_container}>
                            <div className={style.household_applicant_member_container__icon}>
                                <FImage imageUrl={'/lb_household_income_person_bw.svg'} alt="person" />
                            </div>
                            <div className={style.household_member_review_container__member_group}>
                                <LBText
                                    sx={{
                                        textAlignLast: 'left',
                                        whiteSpace: 'pre-line',
                                        fontFamily: fontFamily.body,
                                        fontWeight: '700',
                                        lineHeight: '140%',
                                        color: theme.colors.gray400,
                                        fontSize: '14px',
                                        marginBottom: '5px',
                                    }}
                                    dangerouslySetInnerHTML={{
                                        __html: t('long_beach_applicant_name_label_relationship', {
                                            name: `${member?.name}`.toUpperCase(),
                                            relationship: t(`${member?.relation}`),
                                        }),
                                    }}
                                />
                                <LBText
                                    sx={{
                                        textAlignLast: 'left',
                                        whiteSpace: 'pre-line',
                                        fontFamily: fontFamily.body,
                                        fontWeight: '400',
                                        lineHeight: '140%',
                                        color: theme.colors.gray400,
                                        fontSize: '14px',
                                        marginBottom: '0px',
                                    }}
                                    dangerouslySetInnerHTML={{
                                        __html: `${t('dob_placeholder')}: ${formattedDateMMDDYYYY(member?.dob)} (${t(
                                            'ssi_benefits_summary_table_key_age'
                                        )} ${getAge(member?.dob)})`,
                                    }}
                                />
                                <LBText
                                    sx={{
                                        textAlignLast: 'left',
                                        whiteSpace: 'pre-line',
                                        fontFamily: fontFamily.body,
                                        fontWeight: '400',
                                        lineHeight: '140%',
                                        color: theme.colors.gray400,
                                        fontSize: '14px',
                                        marginBottom: '0px',
                                        marginTop: '0px !important',
                                    }}
                                    dangerouslySetInnerHTML={{
                                        __html: t(
                                            `${
                                                member?.blindDisabled
                                                    ? 'ssi_benefits_wizard_blind_disabled_yes'
                                                    : 'ssi_benefits_wizard_blind_disabled_no'
                                            }`
                                        ),
                                    }}
                                />
                                <LBText
                                    sx={{
                                        textAlignLast: 'left',
                                        whiteSpace: 'pre-line',
                                        fontFamily: fontFamily.body,
                                        fontWeight: '400',
                                        lineHeight: '140%',
                                        color: theme.colors.gray400,
                                        fontSize: '14px',
                                        marginBottom: '0px',
                                        marginTop: '0px !important',
                                    }}
                                    dangerouslySetInnerHTML={{
                                        __html:
                                            member?.earnedIncome !== '0' && member?.unearnedIncome !== '0'
                                                ? t(`lb_household_income_review_income_earner_label`)
                                                : null,
                                    }}
                                />
                                <LBText
                                    sx={{
                                        textAlignLast: 'left',
                                        whiteSpace: 'pre-line',
                                        fontFamily: fontFamily.body,
                                        fontWeight: '400',
                                        lineHeight: '140%',
                                        color: theme.colors.gray400,
                                        fontSize: '14px',
                                        marginBottom: '0px',
                                        marginTop: '0px !important',
                                    }}
                                    dangerouslySetInnerHTML={{
                                        __html: `${t('ssi_benefits_summary_table_key_earned_income')}: ${
                                            member?.earnedIncome !== null ? formatMoney(member?.earnedIncome) : '-'
                                        }`,
                                    }}
                                />
                                <LBText
                                    sx={{
                                        textAlignLast: 'left',
                                        whiteSpace: 'pre-line',
                                        fontFamily: fontFamily.body,
                                        fontWeight: '400',
                                        lineHeight: '140%',
                                        color: theme.colors.gray400,
                                        fontSize: '14px',
                                        marginBottom: '0px',
                                        marginTop: '0px !important',
                                    }}
                                    dangerouslySetInnerHTML={{
                                        __html: `${t('ssi_benefits_summary_table_key_unearned_income')}: ${
                                            member?.unearnedIncome !== null ? formatMoney(member?.unearnedIncome) : '-'
                                        }`,
                                    }}
                                />
                            </div>
                        </div>
                    ))}
                    <LBTextHeader
                        sx={{
                            textAlign: 'left',
                            fontSize: '32px',
                            color: theme.colors.blackLabelfont,
                            lineHeight: '120%',
                            fontFamily: fontFamily.header,
                            marginBottom: '24px',
                            marginTop: '24px',
                        }}
                    >
                        {t('ssi_benefits_wizard_household_members_header_review')}
                    </LBTextHeader>
                    <LBText
                        sx={{
                            textAlignLast: 'left',
                            whiteSpace: 'pre-line',
                            fontFamily: fontFamily.header,
                            fontWeight: '800',
                            lineHeight: '140%',
                            color: '#6C6C6C',
                            fontSize: '16px',
                            marginBottom: '24px',
                        }}
                    >
                        {t('long_beach_review_summary_benefits_subheader')}
                    </LBText>
                    <LBText
                        sx={{
                            textAlignLast: 'left',
                            whiteSpace: 'pre-line',
                            fontFamily: fontFamily.body,
                            fontWeight: '400',
                            lineHeight: '140%',
                            color: '#6C6C6C',
                            fontSize: '14px',
                        }}
                        dangerouslySetInnerHTML={{
                            __html: t('long_beach_review_applicant_benefits_summary_details', {
                                SSI_answer: SSIBenefitsAnswer ? `${t(SSIBenefitsAnswer)}` : '-',
                                HUD_HOUSING_answer: HUDHousingBenefitsAnswer ? HUDHousingBenefitsAnswer : '-',
                                CAL_FRESH_answer: CalFreshBenefitsAnswer ? CalFreshBenefitsAnswer : '-',
                                CAL_WORKS_answer: CalWorksBenefitsAnswer ? CalWorksBenefitsAnswer : '-',
                                HEAD_START_answer: HeadStartBenefitsAnswer ? HeadStartBenefitsAnswer : '-',
                                ACP_answer: ACPBenefitsAnswer ? ACPBenefitsAnswer : '-',
                            }),
                        }}
                    />
                    <LBTextHeader
                        sx={{
                            textAlign: 'left',
                            fontSize: '32px',
                            color: theme.colors.blackLabelfont,
                            lineHeight: '120%',
                            fontFamily: fontFamily.header,
                            marginBottom: '24px',
                            marginTop: '24px',
                        }}
                    >
                        {t('long_beach_documents_header')}
                    </LBTextHeader>
                    <LBText
                        sx={{
                            textAlignLast: 'left',
                            whiteSpace: 'pre-line',
                            fontFamily: fontFamily.body,
                            fontWeight: '400',
                            lineHeight: '140%',
                            color: '#6C6C6C',
                            fontSize: '14px',
                        }}
                        dangerouslySetInnerHTML={{
                            __html: `<b>${t('PROOF_OF_RESIDENCY')}</b>: ${ProofOfResidenceDocumentAnswer}`,
                        }}
                    />
                    {applicationContext.documents.map(
                        (document, index) =>
                            document.node.documentType === 'PROOF_OF_RESIDENCY' && (
                                <LBText
                                    sx={{
                                        marginTop: '0px !important',
                                        textAlignLast: 'left',
                                        whiteSpace: 'pre-line',
                                        fontFamily: fontFamily.body,
                                        fontWeight: '400',
                                        lineHeight: '140%',
                                        color: '#6C6C6C',
                                        fontSize: '14px',
                                    }}
                                    dangerouslySetInnerHTML={{
                                        __html: `• ${document.node.documentOriginalName}`,
                                    }}
                                />
                            )
                    )}
                    <LBText
                        sx={{
                            textAlignLast: 'left',
                            whiteSpace: 'pre-line',
                            fontFamily: fontFamily.body,
                            fontWeight: '400',
                            lineHeight: '140%',
                            color: '#6C6C6C',
                            fontSize: '14px',
                        }}
                        dangerouslySetInnerHTML={{
                            __html: `<b>${t('PROOF_OF_INCOME')}</b>: ${ProofOfIncomeDocumentAnswer}`,
                        }}
                    />
                    {applicationContext.documents.map(
                        (document, index) =>
                            document.node.documentType === 'PROOF_OF_INCOME' && (
                                <LBText
                                    sx={{
                                        marginTop: '0px !important',
                                        textAlignLast: 'left',
                                        whiteSpace: 'pre-line',
                                        fontFamily: fontFamily.body,
                                        fontWeight: '400',
                                        lineHeight: '140%',
                                        color: '#6C6C6C',
                                        fontSize: '14px',
                                    }}
                                    dangerouslySetInnerHTML={{
                                        __html: `• ${
                                            document.node.householdMember === null
                                                ? `${applicationContext?.profile?.firstName} ${applicationContext?.profile?.lastName}`
                                                : applicationContext.householdMembers.find(
                                                      (member) => member.id === document.node.householdMember.id
                                                  )?.name
                                        }: ${document.node.documentOriginalName}`,
                                    }}
                                />
                            )
                    )}

                    <LBButton
                        type={'submit'}
                        id={'continueButton'}
                        title={t('continue')}
                        width={'100%'}
                        mt={7}
                        mb={5}
                        textStyle={{
                            fontWeight: 700,
                            fontSize: '14px',
                            lineHeight: '140%',
                            fontFamily: fontFamily.body,
                            color: theme.colors.white,
                        }}
                    >
                        {t('continue')}
                    </LBButton>
                    {errors.reviewSummary && (
                        <SInputError
                            sx={{
                                fontFamily: fontFamily.body,
                                color: theme.colors.redError,
                            }}
                            forElementId={'continueButton'}
                        >
                            {t(errors.reviewSummary?.message)}
                        </SInputError>
                    )}
                </SBox>
            </div>
        </Container>
    )
}

export default ReviewSummary
